// Constants
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
import { RecurrenceEndType } from "../../../service/planner/enumerations/IPlannerEnumerations";
import { RecurrencePattern } from "./RecurrencePattern";
import { RecurrenceRange } from "./RecurrenceRange";
// Utilities
import { getDiff, getDiffTemporalAndArrayCustomizer } from "@ms/uno-utilities/lib/local/ObjectUtilities";
import isEmpty from "lodash/isEmpty";
import { Temporal } from "temporal-polyfill-lite";
import { deserializeRecurrenceUtcDateTime, serializeRecurrenceUtcDateTime } from "./utilities/RecurrenceUtcDateTimeUtilities";
/**
 * The recurrence schedule implementation.
 */ export class RecurrenceSchedule {
    static fromResource(resource) {
        return new RecurrenceSchedule({
            pattern: RecurrencePattern.fromResource(resource.pattern),
            range: RecurrenceRange.fromResource({
                recurrenceEndType: RecurrenceEndType.EndOfPlan,
                endDate: null,
                countOfInstances: null,
                startDate: resource.patternStartDateTime,
                startUtcDateAndTime: resource.patternStartUtcDateTime
            }),
            nextOccurrenceDateTime: !isEmpty(resource.nextOccurrenceDateTime) ? Temporal.Instant.from(resource.nextOccurrenceDateTime) : null,
            nextOccurrenceUtcDateTime: deserializeRecurrenceUtcDateTime(resource.nextOccurrenceUtcDateTime)
        });
    }
    getClone() {
        return new RecurrenceSchedule({
            pattern: this.pattern.getClone(),
            range: this.range.getClone(),
            nextOccurrenceDateTime: this.nextOccurrenceDateTime != null ? Temporal.Instant.from(this.nextOccurrenceDateTime) : null,
            nextOccurrenceUtcDateTime: this.nextOccurrenceUtcDateTime != null ? Temporal.Instant.from(this.nextOccurrenceUtcDateTime) : null
        });
    }
    forSerialization() {
        const range = this.range.forSerialization();
        return {
            pattern: this.pattern.forSerialization(),
            patternStartDateTime: range.startDate,
            nextOccurrenceDateTime: this.nextOccurrenceDateTime?.toString() ?? "",
            patternStartUtcDateTime: range.startUtcDateAndTime,
            nextOccurrenceUtcDateTime: serializeRecurrenceUtcDateTime(this.nextOccurrenceUtcDateTime)
        };
    }
    forCreate() {
        const range = this.range.forSerialization();
        return {
            pattern: this.pattern.forSerialization(),
            patternStartDateTime: range.startDate,
            ...AppContext?.appConfiguration?.flights?.EnableEvoTime === true ? {
                patternStartUtcDateTime: range.startUtcDateAndTime
            } : {}
        };
    }
    forUpdate(source) {
        // Use getDiff to get the differences between current and source entity
        const diff = getDiff(source, this, getDiffTemporalAndArrayCustomizer);
        // If no differences found, return undefined
        if (isEmpty(diff)) {
            return undefined;
        }
        const resource = {};
        if (diff.pattern) {
            // Pattern is immutable on the service. The entire object has to be sent when changed.
            resource.pattern = this.pattern.forSerialization();
        }
        // UTC-only range changes must not trigger a legacy date PATCH, even when the UTC flight is off.
        const { startUtcDateAndTime, ...legacyRangeChanges } = diff.range ?? {};
        if (!isEmpty(legacyRangeChanges)) {
            resource.patternStartDateTime = this.range.forSerialization().startDate;
        }
        if (startUtcDateAndTime !== undefined && AppContext?.appConfiguration?.flights?.EnableEvoTime === true) {
            const value = serializeRecurrenceUtcDateTime(this.range.startUtcDateAndTime);
            if (value !== undefined) {
                resource.patternStartUtcDateTime = value;
            }
        }
        if (!isEmpty(resource)) {
            return resource;
        }
    }
    setRecurrencePattern(pattern) {
        return new RecurrenceSchedule({
            pattern,
            range: this.range.getClone(),
            nextOccurrenceDateTime: this.nextOccurrenceDateTime != null ? Temporal.Instant.from(this.nextOccurrenceDateTime) : null,
            nextOccurrenceUtcDateTime: this.nextOccurrenceUtcDateTime != null ? Temporal.Instant.from(this.nextOccurrenceUtcDateTime) : null
        });
    }
    setRecurrenceRange(range) {
        return new RecurrenceSchedule({
            pattern: this.pattern.getClone(),
            range,
            nextOccurrenceDateTime: this.nextOccurrenceDateTime != null ? Temporal.Instant.from(this.nextOccurrenceDateTime) : null,
            nextOccurrenceUtcDateTime: this.nextOccurrenceUtcDateTime != null ? Temporal.Instant.from(this.nextOccurrenceUtcDateTime) : null
        });
    }
    generateHash() {
        const values = [
            this.pattern.generateHash(),
            this.range.generateHash(),
            this.nextOccurrenceDateTime?.toString() ?? ""
        ];
        if (this.nextOccurrenceUtcDateTime != null) {
            values.push(`nextOccurrenceUtcDateTime=${this.nextOccurrenceUtcDateTime.toString()}`);
        }
        return values.join(",");
    }
    constructor(propertyBag){
        this.pattern = propertyBag.pattern;
        this.range = propertyBag.range;
        this.nextOccurrenceDateTime = propertyBag.nextOccurrenceDateTime;
        this.nextOccurrenceUtcDateTime = propertyBag.nextOccurrenceUtcDateTime ?? null;
    }
}
